import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import api from "../services/api";
import { fetchStockCatalogs } from "../services/stockCatalogs";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";

const initialForm = {
  name: "",
  sku: "",
  service_tag: "",
  express_service_code: "",
  category: "",
  category_id: "",
  subcategory: "",
  subcategory_id: "",
  description: "",
  quantity: 0,
  unit: "Un",
  minimum_stock: 0,
  reorder_point: 0,
  ideal_stock: 0,
  average_cost: 0,
  supplier: "",
  supplier_id: "",
  location: "",
  location_id: "",
  lead_time_days: 0,
  is_active: true,
};

const NewStockItem = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [catalogs, setCatalogs] = useState({
    categories: [],
    subcategories: [],
    suppliers: [],
    locations: [],
  });
  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const data = await fetchStockCatalogs(true);
        setCatalogs(data);
      } catch (error) {
        console.error("Erro ao carregar cadastros do estoque:", error);
      }
    };

    loadCatalogs();
  }, []);

  const handleChange = (key, value) => {
    setFormData((prev) => {
      if (key === "category_id") {
        return { ...prev, category_id: value, subcategory_id: "" };
      }
      return { ...prev, [key]: value };
    });
  };

  const availableSubcategories = catalogs.subcategories.filter((subcategory) => {
    if (!formData.category_id) return true;
    return String(subcategory.category_id) === String(formData.category_id);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post("/stock/items", {
        ...formData,
        quantity: Number(formData.quantity || 0),
        category_id: formData.category_id ? Number(formData.category_id) : null,
        subcategory_id: formData.subcategory_id ? Number(formData.subcategory_id) : null,
        minimum_stock: Number(formData.minimum_stock || 0),
        reorder_point: Number(formData.reorder_point || 0),
        ideal_stock: Number(formData.ideal_stock || 0),
        average_cost: Number(formData.average_cost || 0),
        supplier_id: formData.supplier_id ? Number(formData.supplier_id) : null,
        location_id: formData.location_id ? Number(formData.location_id) : null,
        lead_time_days: Number(formData.lead_time_days || 0),
      });
      navigate("/stock");
    } catch (error) {
      console.error("Error creating stock item:", error.response?.data || error);
      alert(error.response?.data?.detail || "Erro ao criar item de estoque");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="new-stock-item-page">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:space-x-4">
          <Button variant="ghost" aria-label="Voltar" onClick={() => navigate("/stock")} data-testid="back-to-stock-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="new-stock-item-title">
              Novo Item de Estoque
            </h1>
            <p className="text-gray-500">Cadastre materiais com parâmetros completos de controle.</p>
          </div>
          <div className="md:ml-auto">
            <Button variant="outline" onClick={() => navigate("/stock/catalogs")}>
              Cadastros de apoio
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Cadastro do Item</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Nome do Item *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder="Ex: Mouse USB, Toner HP 85A, Cabo HDMI"
                    required
                    data-testid="stock-item-name-input"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">SKU / Código</label>
                  <Input
                    value={formData.sku}
                    onChange={(e) => handleChange("sku", e.target.value)}
                    placeholder="Ex: MOU-USB-001"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Service Tag <span className="text-gray-400">(opcional)</span>
                  </label>
                  <Input
                    value={formData.service_tag}
                    onChange={(e) => handleChange("service_tag", e.target.value)}
                    placeholder="Ex: 7XYZ123 (notebooks/PCs)"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Express Service Code <span className="text-gray-400">(opcional)</span>
                  </label>
                  <Input
                    value={formData.express_service_code}
                    onChange={(e) => handleChange("express_service_code", e.target.value)}
                    placeholder="Ex: 12345678901"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Categoria</label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => handleChange("category_id", e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="stock-item-category-input"
                  >
                    <option value="">Sem categoria</option>
                    {catalogs.categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Unidade</label>
                  <select
                    value={formData.unit}
                    onChange={(e) => handleChange("unit", e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="stock-item-unit-select"
                  >
                    <option value="Un">Unidade (Un)</option>
                    <option value="Cx">Caixa (Cx)</option>
                    <option value="Pç">Peça (Pç)</option>
                    <option value="Pc">Pacote (Pc)</option>
                    <option value="Kg">Quilo (Kg)</option>
                    <option value="L">Litro (L)</option>
                    <option value="M">Metro (M)</option>
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Subcategoria</label>
                  <select
                    value={formData.subcategory_id}
                    onChange={(e) => handleChange("subcategory_id", e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Sem subcategoria</option>
                    {availableSubcategories.map((subcategory) => (
                      <option key={subcategory.id} value={subcategory.id}>
                        {subcategory.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Quantidade Inicial</label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.quantity}
                    onChange={(e) => handleChange("quantity", e.target.value)}
                    data-testid="stock-item-quantity-input"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Custo Médio Inicial</label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.average_cost}
                    onChange={(e) => handleChange("average_cost", e.target.value)}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Estoque Mínimo</label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.minimum_stock}
                    onChange={(e) => handleChange("minimum_stock", e.target.value)}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Ponto de Pedido</label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.reorder_point}
                    onChange={(e) => handleChange("reorder_point", e.target.value)}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Estoque Ideal</label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.ideal_stock}
                    onChange={(e) => handleChange("ideal_stock", e.target.value)}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Lead Time (dias)</label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={formData.lead_time_days}
                    onChange={(e) => handleChange("lead_time_days", e.target.value)}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Fornecedor</label>
                  <select
                    value={formData.supplier_id}
                    onChange={(e) => handleChange("supplier_id", e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Sem fornecedor</option>
                    {catalogs.suppliers.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Localização</label>
                  <select
                    value={formData.location_id}
                    onChange={(e) => handleChange("location_id", e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Sem localização</option>
                    {catalogs.locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Status</label>
                  <select
                    value={formData.is_active ? "true" : "false"}
                    onChange={(e) => handleChange("is_active", e.target.value === "true")}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="true">Ativo</option>
                    <option value="false">Inativo</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700">Descrição</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                    className="min-h-[110px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Observações técnicas, aplicação do material, especificação..."
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => navigate("/stock")} data-testid="cancel-stock-item-btn">
                  Cancelar
                </Button>
                <Button type="submit" disabled={loading} data-testid="submit-stock-item-btn">
                  {loading ? "Criando..." : "Criar Item"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default NewStockItem;
